import { Dispatch, FC, Fragment, SyntheticEvent, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Button, ButtonGroup, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl,
  FormControlLabel, Input, ListItemText, Stack, Switch, TableCell, TableRow,
  Typography
} from "@mui/material";
import { cellStyle } from "../Pays/RowPays";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingButton } from "@mui/lab";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { AxiosInstance } from "axios";
import SaveIcon from '@mui/icons-material/Save';
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
import { btnStyle } from "../user/Tabs/utilisateur/UtilisateurRow";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { DataRef } from "../../../../types/DataRef";
import { AlertType } from "../../../../types/AlertType";
import CheckAttr from "../../../../utils/CheckAttr";
import { useNavigate } from 'react-router';
import { ClientInterface } from "./InterfaceClient";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DesactiveDateModal from "../../../element/DesactiveDateModal";
import ChangeStatusConfirmation from "../../../element/ChangeStatusConfirmation";

interface RowClientProps {
  row: ClientInterface;
  isDesktopOrLaptop: boolean;
  http: AxiosInstance;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
  expanded: number | false;
  setExpanded: Dispatch<React.SetStateAction<number | false>>;
  handleReload: () => void;
}

const RowClient: FC<RowClientProps> = ({
  row,
  http,
  isDesktopOrLaptop,
  setOpenSnack,
  setAlerteMessage,
  setAlertType,
  handleReload,
  expanded,
  setExpanded
}) => {
  const navigate = useNavigate();

  const [modify, setModify] = useState<boolean>(false);
  const [desactiveDateModal, setDesactiveDateModal] = useState<boolean>(false);
  const [changeStatut, setChangeStatut] = useState<boolean>(false);
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [errorInput, setErrorInput] = useState<boolean>(false);
  const [clientToSave, setClientToSave] = useState<DataRef>({ ...row });

  const handleDelete = async () => {
    setDeleteLoading(true);
    await http.delete(`client/delete/${row?.id}`)
      .then(() => {
        setOpenModalDelete(false);
        setAlerteMessage("Suppresion efféctuée !");
        setAlertType("success");
        setOpenSnack(true);
        handleReload();
      })
      .catch(error => {
        console.log(error);
        setAlertType("error");
        setOpenSnack(true);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleChange = (e: any) => {
    const { value } = e.target;
    setClientToSave({
      ...clientToSave,
      libelle: value
    });
  }

  const handleSave = () => {
    setErrorInput(false);

    if (CheckAttr(clientToSave?.libelle)) {
      http.put(`client/update`, clientToSave)
        .then(() => {
          setAlerteMessage("Mis à jour effectuée !");
          setAlertType("success");
          setOpenSnack(true);
          setModify(false);
          handleReload();
        })
        .catch(error => {
          console.log(error);
          setAlertType("error");
          setOpenSnack(true);
        })
        .finally(() => {
          setDeleteLoading(false);
        });
    } else {
      setErrorInput(true);
      setAlerteMessage("Le client saisie est invalide !");
      setAlertType("warning");
      setOpenSnack(true);
    }
  };

  const handleCancel = () => {
    setClientToSave({ ...clientToSave, libelle: row?.libelle });
    setModify(false);
  }

  const redirectToOperation = () => {
    navigate('/operations', {
      state: {
        client: clientToSave
      }
    });
  };

  const handleChangeAccordionValue = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
    let val: number | false;
    if (isExpanded) {
      val = panel;
    } else {
      val = false;
      setModify(false);
    }
    setExpanded(val);
  };

  const handleChangeState = () => {
    row?.actif ? setDesactiveDateModal(true) : setChangeStatut(true);
  };

  const handleClose1 = () => {
    setChangeStatut(false);
  };

  const handleClose2 = () => {
    setDesactiveDateModal(false);
  };


  const elementToShow = modify ? (
    <FormControl variant="outlined" sx={{ maxWidth: 120 }}>
      <Input
        sx={{
          mt: 0.5,
          color: "white",
          fontSize: 11,
          ":before": { borderBottomColor: "white" },
          ":after": { borderBottomColor: "#fff" },
        }}
        autoComplete="off"
        error={errorInput}
        color="warning"
        name="pays"
        value={clientToSave?.libelle}
        onChange={handleChange}
        size="small"
        autoFocus
      />
    </FormControl>
  ) : clientToSave?.libelle;

  const btnToShow = modify ? (
    <Fragment>
      <Button
        variant="outlined"
        sx={btnStyle({
          bgColor: "#070d23",
          hoverColor: "Gray",
          color: "#fff",
        })}
        startIcon={<SaveIcon />}
        onClick={() => handleSave()}
      >
        Enregistrer
      </Button>
      <Button
        variant="outlined"
        sx={btnStyle({
          bgColor: "Gray",
          hoverColor: "#FC8404",
          color: "#fff",
        })}
        startIcon={<ExitToAppIcon />}
        onClick={() => handleCancel()}
      >
        Annuler
      </Button>
    </Fragment>
  ) : (
    <Button
      variant="outlined"
      sx={
        btnStyle({
          bgColor: "#070d23",
          hoverColor: "Gray",
          color: "#fff",
        })
      }
      startIcon={<BorderColorIcon />}
      onClick={(e) => setModify(!modify)}
      disabled={!row?.actif}
    >
      Modifier
    </Button >
  );

  const confirmMsgForStatut = `Vous êtes sur le point ${row?.actif ? "de désactiver" : "d'activer"} ce client ainsi que leur opération et article,
                              êtes-vous sûr de vouloir poursuivre cette action ?`;

  const url = `client/change-statut/${row?.id}`;

  return (
    <>
      {isDesktopOrLaptop ? (
        <TableRow>
          <TableCell component="th" scope="row" style={cellStyle} align="center">
            {elementToShow}
          </TableCell>
          <TableCell style={cellStyle} align="center">
            <Stack spacing={2} direction="row" display="flex" justifyContent="center">
              {btnToShow}
              <Button
                size="small"
                variant="outlined"
                sx={btnStyle({
                  bgColor: "#FF0000",
                  hoverColor: "#FE3737",
                })}
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  setOpenModalDelete(true);
                }}
                disabled={!row?.actif}
              >
                Supprimer
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<DynamicFormIcon />}
                sx={{ textTransform: 'none' }}
                onClick={() => redirectToOperation()}
                disabled={!row?.actif}
              >
                Opération
              </Button>

              <FormControlLabel
                sx={{ mt: 1, color: '#818181' }}
                control={<Switch
                  name="consentement"
                  checked={row?.actif}
                  onChange={() => handleChangeState()}
                  color="warning"
                  size="small"
                  inputProps={{ 'aria-label': 'controlled' }}
                />}
                label={row?.actif ? "Activer" : "Désactiver"}
              />
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <Accordion
          expanded={expanded === row?.id}
          onChange={handleChangeAccordionValue(row?.id)}
          sx={{
            borderRadius: 1,
            bgcolor: '#fff',
            mb: 1,
            ml: 0.1
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#080d24' }} />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            {modify ? (
              <FormControl variant="outlined" sx={{ maxWidth: 120, mr: 5 }}>
                <Input
                  sx={{
                    mt: 0.5,
                    color: "#080d24",
                    fontSize: 10,
                    ":before": { borderBottomColor: "#080d24" },
                    ":after": { borderBottomColor: "#080d24" },
                  }}
                  autoComplete="off"
                  error={errorInput}
                  color="warning"
                  name="pays"
                  value={clientToSave?.libelle}
                  onChange={handleChange}
                  size="small"
                  autoFocus
                />
              </FormControl>
            ) : (
              <ListItemText
                primary={clientToSave?.libelle}
                primaryTypographyProps={{
                  fontSize: 12,
                  fontWeight: "small",
                  lineHeight: "15px",
                  mt: 1,
                  mb: "1px",
                }}
                sx={{ my: 0 }}
              />)}

            <FormControlLabel
              sx={{ color: '#fff', fontSize: 6 }}
              control={<Switch
                name="consentement"
                checked={row?.actif}
                onChange={() => handleChangeState()}
                color="warning"
                size="small"
                inputProps={{ 'aria-label': 'controlled' }}
              />}
              label={
                <Typography variant="subtitle2" color='#080d24'>
                  {row?.actif ? "Activer" : "Désactiver"}
                </Typography>
              }
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction='column' spacing={1}>
              {modify ? (
                <ButtonGroup
                  orientation="vertical"
                  aria-label="Vertical button group"
                  variant="contained"
                  color="inherit"
                >
                  <Button
                    variant="contained"
                    sx={btnStyle({
                      bgColor: "#fff",
                      hoverColor: "#fff",
                      color: "#FC8404",
                    })}
                    size='small'
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={() => handleSave()}
                  >
                    Enregistrer
                  </Button>
                  <Button
                    variant="contained"
                    sx={btnStyle({
                      bgColor: "Gray",
                      hoverColor: "#FC8404",
                      color: "#fff"
                    })}
                    size='small'
                    fullWidth
                    startIcon={<ExitToAppIcon />}
                    onClick={() => handleCancel()}
                  >
                    Annuler
                  </Button>
                </ButtonGroup>
              ) : (
                <Button
                  variant="contained"
                  sx={btnStyle({
                    bgColor: "Gray",
                    hoverColor: "Gray",
                    color: "#fff",
                  })}
                  size='small'
                  fullWidth
                  startIcon={<BorderColorIcon />}
                  onClick={() => setModify(!modify)}
                  disabled={!row?.actif}
                >
                  Modifier
                </Button >
              )}
              <Button
                size="small"
                variant="contained"
                sx={btnStyle({
                  bgColor: "#FF0000",
                  hoverColor: "#FE3737",
                })}
                fullWidth
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  setOpenModalDelete(true);
                }}
                disabled={!row?.actif}
              >
                Supprimer
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<DynamicFormIcon />}
                sx={{ textTransform: 'none' }}
                fullWidth
                onClick={() => redirectToOperation()}
                disabled={!row?.actif}
              >
                Opération
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog
        open={openModalDelete}
        // TransitionComponent={Transition}
        keepMounted
        onClose={(e) => setOpenModalDelete(false)}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Vous êtes sur le point de supprimer ce client, êtes-vous sûr de
            vouloir poursuivre cette action ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={() => handleDelete()}
            endIcon={<DeleteIcon />}
            loading={deleteLoading}
            loadingPosition="end"
            sx={{ textTransform: "none" }}
            variant="contained"
            color="warning"
          >
            Oui
          </LoadingButton>
          <Button
            onClick={(e) => setOpenModalDelete(false)}
            variant="contained"
            color="inherit"
            sx={{ textTransform: "none" }}
            startIcon={<ExitToAppIcon />}
          >
            Non
          </Button>
        </DialogActions>
      </Dialog>

      <ChangeStatusConfirmation
        confirmMessage={confirmMsgForStatut}
        open={changeStatut}
        handleClose1={handleClose1}
        http={http}
        url={url}
        setOpenSnack={setOpenSnack}
        setAlerteMessage={setAlerteMessage}
        setAlertType={setAlertType}
        handleReload={handleReload}
      />

      <DesactiveDateModal
        confirmMessage={confirmMsgForStatut}
        desactiveDateModal={desactiveDateModal}
        handleClose2={handleClose2}
        url={url}
        http={http}
        setOpenSnack={setOpenSnack}
        setAlerteMessage={setAlerteMessage}
        setAlertType={setAlertType}
        handleReload={handleReload}
      />
    </>
  );
};

export default RowClient;
